/**
 * The first viewport — PRD §1.
 *
 * "The first viewport contains the audience promise, one short explanation, an
 * open-text brief box, and one primary action. Do not put a preliminary
 * registration form or a 'try an example' process on this screen."
 *
 * So: no email field, no account, no example buttons, no beat picker. The
 * visitor types their own brief or nothing happens.
 */

import React, { useRef, useState } from 'react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import LiveStoriesPanel from './LiveStoriesPanel';
import CountedTextarea from './CountedTextarea';
import { copyFor } from './audienceCopy';

/**
 * One mark per capability, keyed on the copy's own stable keys rather than
 * on position — the three bodies are audience-specific and could be
 * reordered, and an icon that drifts onto the wrong item is worse than none.
 */
const CAPABILITY_ICONS = {
  deeperDiscovery: TravelExploreOutlinedIcon,
  fitYourWorkflow: HubOutlinedIcon,
  moreWaysToWork: InsightsOutlinedIcon,
};

export default function LandingStep({
  audience, brief, onBriefChange, onSubmit, error, submitting, plain = false,
}) {
  const copy = copyFor(audience);
  const fieldRef = useRef(null);
  const [hasLive, setHasLive] = useState(false);

  const submit = () => {
    if (submitting) return;
    if (!String(brief || '').trim()) {
      // §1 — "On empty submission, ask for a short description and keep focus
      // in the field."
      onSubmit();
      fieldRef.current?.focus();
      return;
    }
    onSubmit();
  };

  // The plain variant has no second column at all, so the grid is never a
  // split — the brief box takes the full measure, as in the mockup.
  const splitClass = (hasLive && !plain)
    ? 'cmp-split cmp-split--landing'
    : 'cmp-split cmp-split--landing cmp-split--single';

  return (
    <>
      {/* The hero spans the full width, above the columns. Inside the left
          column it was wrapping "Find stories your audience will care
          about." onto two lines against a 420px panel that did not need the
          space at that height. */}
      <p className="cmp-eyebrow">Alchemiq</p>
      <h1 className="cmp-title">{copy.headline}</h1>
      <p className="cmp-lede">{copy.support}</p>

      <div className={splitClass}>
        <div className="cmp-field">
          <label className="cmp-label" htmlFor="cmp-brief">{copy.briefLabel}</label>
          <p className="cmp-help">{copy.briefHelper}</p>
          <CountedTextarea
            id="cmp-brief"
            ref={fieldRef}
            wrapClassName="cmp-textarea-wrap"
            className={`cmp-textarea ${error ? 'cmp-textarea--error' : ''}`}
            counterClassName="cmp-counter"
            value={brief}
            onChange={onBriefChange}
            aria-describedby="cmp-brief-examples"
          >
            {/* §1 — two grey examples INSIDE the box, as illustrative
                placeholder text. They are not buttons and do not populate the
                field on click (the CSS makes them click-through), and they
                disappear the moment the visitor types, the normal way. Rendered
                as an overlay rather than as `placeholder` because the attribute
                cannot hold two separate paragraphs and wraps them into one
                unreadable block on a phone. */}
            {!brief && (
              <div className="cmp-placeholder" id="cmp-brief-examples" aria-hidden="true">
                {copy.briefExamples.map((example) => <p key={example}>{example}</p>)}
              </div>
            )}
          </CountedTextarea>
          {error && <div className="cmp-error">{error}</div>}
        </div>

        {/* The right column is the live feed, and nothing else.
          It used to open with a restated promise — "Discover stories worth
          pursuing" over "Describe your coverage and audience" — directly
          beside a headline that already said the first and a field label
          that already said the second. Two sentences of the same thing, in
          a column, with a rule over them. It is gone.
          The panel renders nothing when the feed is unreachable or empty
          (see LiveStoriesPanel on why it must never invent rows), and the
          grid collapses to one column when it does, so the form takes the
          full measure rather than sitting next to a hole. */}
        {!plain && <LiveStoriesPanel onHasStories={setHasLive} />}
      </div>

      {/* Outside the split, on a grid with the SAME tracks — so the action
          stays centred over the field rather than over the whole page, and
          the panel above can bottom-align against the textarea instead of
          against the button. */}
      <div className={(hasLive && !plain) ? 'cmp-landing-actions' : 'cmp-landing-actions cmp-landing-actions--single'}>
        <div className="cmp-actions cmp-actions--centered">
          <button type="button" className="cmp-btn cmp-btn--primary cmp-btn--lg" onClick={submit} disabled={submitting}>
            {copy.primaryAction}
            <ArrowForwardIcon style={{ fontSize: 18 }} />
          </button>
        </div>
      </div>
    </>
  );
}

/**
 * What the rest of Alchemiq does, under the fold.
 *
 * §1 governs the FIRST VIEWPORT, and that is untouched: promise, one
 * explanation, brief box, one action. This sits below it, because the page
 * otherwise ended a few hundred pixels after the CTA in empty canvas — and
 * §3/§4 want the wider platform discoverable anyway.
 *
 * Deliberately the SAME approved capability copy the report-wait screen
 * uses. Nothing here claims anything that has not been signed off, and
 * nothing competes with the primary action: it is below it, quiet, and has
 * no action of its own.
 */
export function LandingCapabilities({ audience, plain = false }) {
  // Hidden entirely in the plain variant — it is the first of the three
  // sections Or asked to remove.
  if (plain) return null;
  const copy = copyFor(audience);
  const items = Object.entries(copy.capabilities || {});
  if (items.length === 0) return null;
  return (
    <section className="cmp-capability-strip">
      <p className="cmp-capability-strip-label">Alchemiq beyond this list</p>
      <div className="cmp-capability-grid">
        {items.map(([key, item]) => {
          const Icon = CAPABILITY_ICONS[key];
          return (
            <div key={item.title}>
              {Icon && (
                <Icon className="cmp-capability-icon" style={{ fontSize: 32 }} aria-hidden />
              )}
              <h3 className="cmp-capability-item-title">{item.title}</h3>
              <p className="cmp-capability-item-body">{item.body}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}
